/**
 * Admin Orcaloops credential RPC definitions (OL-63-links).
 *
 * The operator's half of the vault, and the whole of it: **an operator can mint
 * a person a collection link and see THAT a credential exists; they can never
 * read one, and they can never redeem one.**
 *
 * Both halves of that are mechanical rather than a handler convention:
 *
 *  - the wire row is `Credential.json`, and OL-63 declared `access_token`,
 *    `refresh_token` and `secret_fields` `Model.Sensitive`, so those keys are
 *    not in the schema at all — a future handler edit cannot serve one. Nor is
 *    any `*_token_hash` here: this group never returns a link or lease row;
 *  - redemption lives only in the actor-scoped `credentials.*` group, whose
 *    `requireCredentialOwner` has NO admin short-circuit. Customer membership
 *    authorizes placement, never impersonation.
 *
 * Same operator-surface rules as `orcaloops-human-waits.rpc.ts`: behind
 * `AdminMiddleware`, explicit `customerId`, every call gated by
 * `requireCustomerMember` from `orcaloops-scope.ts`.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Credential, CredentialKind } from "@fm/db/models/Credential";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { MintCollectionLinkResult } from "../authenticated/orcaloops-credentials.rpc.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

/**
 * The customer whose vault is being listed.
 *
 * @since 1.0.0
 */
export const AdminCredentialListInput = Schema.Struct({
  customerId: Schema.String,
});
export type AdminCredentialListInput = Schema.Schema.Type<typeof AdminCredentialListInput>;

/**
 * Mint a collection link FOR a person.
 *
 * This is the one place a subject id rides a payload, and it is why the method
 * lives on the `AdminMiddleware` group rather than the actor-scoped one: the
 * handler gates on `requireCustomerMember(db, actor, customerId)` AND on
 * `personId` actually being a member of `customerId`, refusing before anything
 * is written. The link still belongs to the target person, and only that person
 * can redeem it.
 *
 * @since 1.0.0
 */
export const AdminMintCollectionLinkInput = Schema.Struct({
  personId: Schema.String,
  customerId: Schema.String,
  kind: CredentialKind,
});
export type AdminMintCollectionLinkInput = Schema.Schema.Type<typeof AdminMintCollectionLinkInput>;

/**
 * Admin Orcaloops credential RPC group.
 *
 * @since 1.0.0
 */
export class AdminOrcaloopsCredentialRpcs extends RpcGroup.make(
  Rpc.make("admin.credentials.list", {
    payload: AdminCredentialListInput,
    success: Schema.Array(Credential.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.credentials.mintCollectionLink", {
    payload: AdminMintCollectionLinkInput,
    success: MintCollectionLinkResult,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
